import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Input } from "../ui/input";
import { cn } from "../../lib/utils";
import { History, Search } from "lucide-react";

/**
 * ProjectNameAutocomplete - Autocomplete input for project names
 * 
 * Features:
 * - Case-insensitive partial matching
 * - Max 5 suggestions shown
 * - Highlights matching text
 * - Keyboard navigation (up/down/enter/escape)
 * - Click outside to close
 * - Caches project list from parent component
 */
export function ProjectNameAutocomplete({
  value,
  onChange,
  projects = [],
  placeholder = "Project name",
  required = false,
  id,
  "data-testid": dataTestId,
}) {
  const [inputValue, setInputValue] = useState(value || "");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const containerRef = useRef(null);

  // Sync internal state with external value
  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  // Get unique project names for suggestions
  const uniqueProjectNames = useMemo(() => {
    const namesSet = new Set();
    projects.forEach((p) => {
      if (p.name) {
        namesSet.add(p.name);
      }
    });
    return Array.from(namesSet).sort((a, b) => a.localeCompare(b));
  }, [projects]);

  // Filter suggestions based on input
  const suggestions = useMemo(() => {
    if (!inputValue.trim()) {
      return [];
    }
    const searchTerm = inputValue.toLowerCase().trim();
    return uniqueProjectNames
      .filter((name) => name.toLowerCase().includes(searchTerm))
      .slice(0, 5);
  }, [inputValue, uniqueProjectNames]);

  // Handle input change
  const handleInputChange = useCallback(
    (e) => {
      const newValue = e.target.value;
      setInputValue(newValue);
      onChange(newValue);
      setIsOpen(newValue.trim().length > 0);
      setHighlightedIndex(-1);
    },
    [onChange]
  );

  // Handle selecting a suggestion
  const handleSelectSuggestion = useCallback(
    (name) => {
      setInputValue(name);
      onChange(name);
      setIsOpen(false);
      setHighlightedIndex(-1);
      inputRef.current?.focus();
    },
    [onChange]
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e) => {
      if (!isOpen || suggestions.length === 0) {
        if (e.key === "ArrowDown" && inputValue.trim()) {
          setIsOpen(true);
        }
        return;
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < suggestions.length - 1 ? prev + 1 : 0
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : suggestions.length - 1
          );
          break;
        case "Enter":
          if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
            e.preventDefault();
            handleSelectSuggestion(suggestions[highlightedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          setIsOpen(false);
          setHighlightedIndex(-1);
          break;
        case "Tab":
          setIsOpen(false);
          setHighlightedIndex(-1);
          break;
        default:
          break;
      }
    },
    [isOpen, suggestions, highlightedIndex, handleSelectSuggestion, inputValue]
  );

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('[role="option"]');
      items[highlightedIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Highlight matching text in suggestion
  const highlightMatch = (text, query) => {
    if (!query.trim()) return text;
    
    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase().trim();
    const startIndex = lowerText.indexOf(lowerQuery);
    
    if (startIndex === -1) return text;
    
    const beforeMatch = text.slice(0, startIndex);
    const match = text.slice(startIndex, startIndex + lowerQuery.length);
    const afterMatch = text.slice(startIndex + lowerQuery.length);
    
    return (
      <>
        {beforeMatch}
        <span className="text-primary font-semibold">{match}</span>
        {afterMatch}
      </>
    );
  };

  const showSuggestions = isOpen && suggestions.length > 0;

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Input
          ref={inputRef}
          id={id}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (inputValue.trim() && suggestions.length > 0) {
              setIsOpen(true);
            }
          }}
          placeholder={placeholder}
          required={required}
          autoComplete="off"
          role="combobox"
          aria-expanded={showSuggestions}
          aria-autocomplete="list"
          aria-controls="project-name-suggestions"
          aria-activedescendant={
            highlightedIndex >= 0 ? `suggestion-${highlightedIndex}` : undefined
          }
          data-testid={dataTestId}
          className="pr-8"
        />
        <Search className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && (
        <div
          ref={listRef}
          id="project-name-suggestions"
          role="listbox"
          className={cn(
            "absolute z-50 w-full mt-1 py-1",
            "bg-popover border border-white/10 rounded-sm shadow-lg",
            "max-h-[200px] overflow-y-auto"
          )}
          data-testid="project-name-suggestions"
        >
          <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-white/5 mb-1 flex items-center gap-1.5">
            <History className="h-3 w-3" />
            Existing Projects
          </div>
          {suggestions.map((name, index) => (
            <div
              key={name}
              id={`suggestion-${index}`}
              role="option"
              aria-selected={index === highlightedIndex}
              onClick={() => handleSelectSuggestion(name)}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={cn(
                "px-3 py-2 text-sm cursor-pointer transition-colors",
                "flex items-center gap-2",
                index === highlightedIndex
                  ? "bg-primary/10 text-primary"
                  : "hover:bg-white/5"
              )}
              data-testid={`suggestion-${index}`}
            >
              <span className="truncate">
                {highlightMatch(name, inputValue)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ProjectNameAutocomplete;
